import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAccessToken } from '../auth/use-access-token.js';
import { submitProfile, type ProfileDraft } from '../api/submit-profile.js';
import { ApiError } from '../api/client.js';
import { useMeContext } from '../api/me-provider.js';
import { BusyLabel } from '../components/busy-label.js';

/**
 * Complete your profile — the account holder's details, collected once.
 *
 * Four fields and no branches. An earlier design had a "I'll be taking lessons
 * myself" checkbox governing whether an account_link was written; the schema
 * says otherwise. account_link records responsibility, and an adult is always
 * responsible for themselves, so the link is written every time. Whether they
 * train is answered by booking a lesson, not by a checkbox that can be wrong
 * three years later.
 *
 * The API validates all of this again and is the authority. What the browser
 * does here is spare a round trip and put the message under the right input;
 * it is not a security boundary and must never be treated as one.
 */

const COUNTRIES = [
  { code: 'US', name: 'United States' },
  { code: 'CA', name: 'Canada' },
] as const;

/**
 * The API answers a refusal with a code. These are the sentences for them.
 *
 * Kept as data rather than a chain of conditionals so that an unmapped code
 * still says something true — see the fallback below — instead of rendering
 * nothing and leaving a form that appears to do nothing when pressed.
 */
const MESSAGES: Record<string, string> = {
  required: 'This is required.',
  too_long: 'This is too long.',
  invalid_date: 'Enter a date as YYYY-MM-DD.',
  future_date: 'That date is in the future.',
  must_be_adult:
    "An account holder must be 18 or over. If you're under 18, ask an adult to create the account and add you as an athlete.",
  unsupported_country: 'We do not yet offer lessons in that country.',
  profile_already_complete: 'This account already has a profile. Reload the page to see it.',
  invalid_body: 'Something went wrong sending the form. Please try again.',
  unauthenticated: 'Your session has expired. Sign out and sign in again.',
};

const EMPTY: ProfileDraft = { firstName: '', lastName: '', dateOfBirth: '', country: 'US' };

export function ProfileRoute() {
  const getToken = useAccessToken();
  const navigate = useNavigate();
  const { state: meState, reload } = useMeContext();
  // A family a coach started (stage C): the coach's spelling of the name and
  // the country, to correct rather than type.
  const started = meState.status === 'ready' ? meState.me.profile_draft : undefined;
  // Whose parent they are, so the coach's spelling is read as theirs and not
  // the child's (ruled 20 October). A second parent's form is unchanged.
  const athlete = started?.athlete_first ?? null;

  const [draft, setDraft] = useState<ProfileDraft>(
    started ? { ...EMPTY, firstName: started.first_name, lastName: started.last_name, country: started.country } : EMPTY,
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<{ field?: string; message: string } | null>(null);

  const set = (key: keyof ProfileDraft) => (event: { target: { value: string } }) => {
    setDraft((current) => ({ ...current, [key]: event.target.value }));
    // Clearing on edit rather than on submit: leaving the previous refusal
    // under a field the person is currently correcting reads as though it is
    // still wrong.
    setError(null);
  };

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (saving) return; // A double-press would be refused by the API, but silently.

    setSaving(true);
    setError(null);

    try {
      await submitProfile(getToken, draft);

      // The reload is what actually dismisses this form. ProfileGate renders it
      // INSTEAD of the route, so navigating changes the URL and nothing else —
      // the gate would still see profile_complete false and show the form again
      // at the new address. Refetching /api/me is what lets it through.
      reload();
      // A claimed family stays where the invitation sent them - the athlete to
      // finish next. Everyone else is done, and goes Home (Matthew, 28
      // October: after the first sign-in's information, Home, not Account).
      if (!started) navigate('/');
    } catch (cause) {
      if (cause instanceof ApiError) {
        setError({
          ...(cause.field ? { field: cause.field } : {}),
          message: MESSAGES[cause.code] ?? `That was refused (${cause.code}).`,
        });
      } else {
        setError({ message: 'The API did not answer. Please try again.' });
      }
      setSaving(false);
    }
  }

  const invalid = (field: string) => (error?.field === field ? { 'data-invalid': true } : {});

  return (
    <>
      <section className="panel">
        <h2>Complete your profile</h2>
        {athlete ? (
          <p className="empty">
            You're the parent or guardian on {athlete}'s account. {athlete}'s details come next.
          </p>
        ) : (
          <p className="empty">
            Booking needs a few details about the account holder — the adult responsible for
            bookings and payment.
          </p>
        )}
      </section>

      <form onSubmit={onSubmit} noValidate>
        <section className="panel">
          {athlete ? <span className="section-label">Adult</span> : null}
          <div className="field" {...invalid('firstName')}>
            <label htmlFor="firstName">First name</label>
            <input
              id="firstName"
              name="firstName"
              autoComplete="given-name"
              value={draft.firstName}
              onChange={set('firstName')}
            />
            {error?.field === 'firstName' ? <span className="hint">{error.message}</span> : null}
          </div>

          <div className="field" {...invalid('lastName')}>
            <label htmlFor="lastName">Last name</label>
            <input
              id="lastName"
              name="lastName"
              autoComplete="family-name"
              value={draft.lastName}
              onChange={set('lastName')}
            />
            {error?.field === 'lastName' ? <span className="hint">{error.message}</span> : null}
          </div>

          <div className="field" {...invalid('dateOfBirth')}>
            <label htmlFor="dateOfBirth">Date of birth</label>
            <input
              id="dateOfBirth"
              name="dateOfBirth"
              type="date"
              autoComplete="bday"
              value={draft.dateOfBirth}
              onChange={set('dateOfBirth')}
            />
            <span className="hint">
              {error?.field === 'dateOfBirth' ? error.message : 'An account holder must be 18 or over.'}
            </span>
          </div>

          <div className="field" {...invalid('country')}>
            <label htmlFor="country">Country</label>
            <select id="country" name="country" value={draft.country} onChange={set('country')}>
              {COUNTRIES.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.name}
                </option>
              ))}
            </select>
            <span className="hint">
              {error?.field === 'country'
                ? error.message
                : 'Decides which services and prices you see. Never guessed from your location.'}
            </span>
          </div>
        </section>

        <button type="submit" className="primary" disabled={saving} aria-busy={saving}>
          <BusyLabel busy={saving} working="Saving…">
            Save and continue
          </BusyLabel>
        </button>

        {/* A refusal with no field belongs to the form, not to an input. */}
        {error && !error.field ? <p className="form-note">{error.message}</p> : null}
      </form>
    </>
  );
}
